import React, { useContext, useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { ArrowLeft, Calendar, CheckSquare, Image, Pencil, PlusMinus, RadioButton, SortAscending, TextT, TrashSimple, NumberSquareSix } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { Header } from '../../components/Header';
import { Project } from '../../types/ProjectType';
import { LoadingScreen } from '../../components/LoadingScreen';
import { TextFieldDataModal } from '../../components/TextFieldDataModal';
import { FieldOptionsDataModal } from '../../components/FieldOptionsDataModal';
import { ImageFieldModal } from '../../components/ImageFieldModal';
import { CounterFieldModal } from '../../components/CounterFieldModal';
import { useTheme } from '../../providers/theme';
import { Field } from '../../types/FieldType';
import { DragNDrop } from '../../types/DragNDrop';
import { FIELD_TYPE } from '../../FieldTypes';
import { TextComponent } from '../../components/TextComponent';
import { FieldsContainer, IconTextContainerButton, InfoContainer } from './styles';
import './ProjectField.styles.css';
import { DatetimeFieldDataModal } from '../../components/DatetimeFieldModal';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { UserDataContext } from '../../providers/userData';
import { DialogAlert } from '../../components/DialogAlert';
import { NumberFieldDataModal } from '../../components/NumberFieldDataField';
import { LabelFieldDataModal } from '../../components/LabelFieldDataModal';

type ProjectFieldProps = {
  project: Project;
  onEditField: Field;
  isLoading: boolean;
  fieldTypeValue: string;
  modalSelect: {
    text: boolean,
    number: boolean,
    image: boolean,
    counter: boolean,
    datetime: boolean,
    options: boolean,
    label: boolean
  };
  isEditFieldModalOpen: boolean;
  fieldsLength: number;

  createField: (fieldType: string, optionName?: string) => void;
  onEdit: (field: Field) => void;
  onDelete: (field: Field, fieldsList: Field[]) => void;
  getProjectData: () => Promise<void>;
  updateFieldOrder: (fieldsList: Field[]) => void;
}

export function ProjectField({
  project,
  onEditField,
  isLoading,
  fieldTypeValue,
  modalSelect,
  isEditFieldModalOpen,
  createField,
  onEdit,
  onDelete,
  getProjectData,
  updateFieldOrder,
  fieldsLength,
}: ProjectFieldProps) {
  const navigate = useNavigate();
  const [isEnableRecordsButton, setIsEnableRecordsButton] = useState(false);
  const [isDisabledOrderButton, setIsDisabledOrderButton] = useState(true);
  const [fieldsList, setFieldsList] = useState<Field[]>([]);
  const { theme } = useTheme();
  const { userData } = useContext(UserDataContext);
  const { contract_id: contractId, id: projectId, title, description, fields } = project;
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [fieldRow, setFieldRow] = useState<Field>();
  const [fieldRowTitle, setFieldRowTitle] = useState<string>();


  useEffect(() => {
    if (fields) {
      const fieldsCopy = JSON.parse(JSON.stringify(fields));
      setFieldsList(fieldsCopy);
      if (fields.length === 0) {
        setIsEnableRecordsButton(true);
      } else {
        setIsEnableRecordsButton(false);
      }
    }
  }, [fields]);

  function reorder(fieldsList: Field[], startIndex: number, endIndex: number) {
    const [removed] = fieldsList.splice(startIndex, 1);
    removed.field.order = endIndex;
    fieldsList.splice(endIndex, 0, removed);
    reorderList(fieldsList, startIndex, endIndex);
  }

  function reorderList(fieldsList:Field[], startIndex: number, endIndex: number) {
    if(startIndex === endIndex) return;
    if(startIndex < endIndex){
      setIsDisabledOrderButton(false);
      for(let i = startIndex; i < endIndex; i++) {
        fieldsList[i].field.order = (fieldsList[i].field.order ?? 0) - 1;
      }
      return;
    } else {
      setIsDisabledOrderButton(false);
      for(let i = endIndex + 1; i < startIndex + 1; i++) {
        fieldsList[i].field.order = (fieldsList[i].field.order ?? 0) + 1;
      }
      return;
    }
  }

  function onDragEnd(result: DragNDrop) {
    if(!result.destination) return;
    reorder(fieldsList, result.source.index, result.destination.index);
  }

  const handleUpdateReorder = () => {
    updateFieldOrder(fieldsList);
    setIsDisabledOrderButton(true);
  };

  const handleDeleteField = (fieldRow: Field) => {
    setDialogOpen(true);
    setFieldRow(fieldRow);
    setFieldRowTitle(fieldRow.title);
  };

  function handleDeleteFieldConfirmed() {
    setDialogOpen(false);
    if(fieldRow){
      onDelete(fieldRow, fieldsList);
    }
  }

  function handleClose() {
    setDialogOpen(false);
  }

  return (
    <>
      {isLoading ? (
        <LoadingScreen isVisible={isLoading} />
      ) : (
        <>
          <Header />

          <InfoContainer>
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 30, flex: 1 }} >
              <Tooltip title='Visualizar Projetos'>
                <ArrowLeft onClick={() => navigate(`/contracts/${contractId}/projects`)} size={30} color={theme.colors.main} cursor='pointer' />
              </Tooltip>
              <TextComponent
                children={title}
              />
              <TextComponent
                children={description}
              />

              {userData.admin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', flex: 2, marginRight: 100, }}>
                  <Button
                    id='fields'
                    onClick={() => isDisabledOrderButton ? alert('Por favor, ordenar a lista.') : handleUpdateReorder()}
                    type='button'
                    color={isDisabledOrderButton ? theme.colors.hoverGreen : theme.colors.green}
                    sxhover={isDisabledOrderButton ? null : theme.colors.hoverGreen}
                    sxwidth='123px'
                    sxgap='5px'
                    $sxdisabled={isDisabledOrderButton}
                  >
                    {t('projects-forms-sort-button')}
                  </Button>
                  <Button
                    id='records'
                    onClick={() => isEnableRecordsButton === true ? alert('Não há registros salvos nesse projeto.') : navigate(`/contracts/${contractId}/project/${projectId}/answers`)}
                    type='button'
                    color={theme.colors.green}
                    sxhover={theme.colors.hoverGreen}
                    sxwidth='123px'
                    sxgap='5px'
                    sxmarginleft='25px'
                  >
                    {t('projects-forms-records-button')}
                  </Button>
                </div>
              )}
            </div>
          </InfoContainer>

          {userData.admin && (
            <FieldsContainer>
              <Button id='newTextField' onClick={() => createField('text')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <TextT size={25} />
                  {t('projects-forms-text')}
                </IconTextContainerButton>
              </Button>
              <Button id='newNumberField' onClick={() => createField('number')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <NumberSquareSix size={25} />
                  {t('projects-forms-number')}
                </IconTextContainerButton>
              </Button>
              <Button id='newCheckboxField' onClick={() => createField('options', 'checkbox')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <CheckSquare size={25} />
                  {t('projects-forms-checkbox')}
                </IconTextContainerButton>
              </Button>
              <Button id='newDropdownField' onClick={() => createField('options', 'dropdown')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <SortAscending size={25} />
                  {t('projects-forms-dropdown')}
                </IconTextContainerButton>
              </Button>
              <Button id='newRadioField' onClick={() => createField('options', 'radio')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <RadioButton size={25} />
                  {t('projects-forms-radio')}
                </IconTextContainerButton>
              </Button>
              <Button id='newImageField' onClick={() => createField('image')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='150px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <Image size={25} />
                  {t('projects-forms-image')}
                </IconTextContainerButton>
              </Button>
              <Button id='newCountField' onClick={() => createField('counter')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <PlusMinus size={25} />
                  {t('projects-forms-counter')}
                </IconTextContainerButton>
              </Button>
              <Button id='newDatetimeField' onClick={() => createField('datetime')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <Calendar size={25} />
                  {t('projects-forms-date')}
                </IconTextContainerButton>
              </Button>
              <Button id='newLabelField' onClick={() => createField('label')} type='button'
                color={theme.colors.blue} sxhover={theme.colors.hoverBlue} sxwidth='130px' sxgap='5px'
              >
                <IconTextContainerButton>
                  <TextT size={25} />
                  {t('projects-forms-label')}
                </IconTextContainerButton>
              </Button>
            </FieldsContainer>
          )}

          <TableContainer sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', mt: 3, mb: 3 }}>
            <Table size='small' sx={{ background: '#F3F3F3', borderRadius: 1, width: '90%' }}>
              <TableHead sx={{ background: '#D9D9D9' }}>
                <TableRow>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-title')}</span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-description')}</span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-type')}</span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-manager')}</span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-order')}</span>
                  </TableCell>
                  <TableCell>
                    <span style={{ fontWeight: 'bold', fontSize: 16 }}>{t('projects-forms-records-actions')}</span>
                  </TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId='fields' type='list' direction='vertical'>
                  {(provided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {fieldsList?.sort((a, b) => (a.field?.order ?? 0) - (b.field?.order ?? 0)).map((item, index) => (
                        <Draggable draggableId={item.field.id} index={index} key={item.field.id}>
                          {(provided) =>(
                            <React.Fragment>
                              <TableRow
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                ref={provided.innerRef}
                              >
                                <TableCell className='fontAdjusment'>
                                  <span>{item.field.title}</span>
                                </TableCell>
                                <TableCell className='fontAdjusment'>
                                  <span>{item.field.description}</span>
                                </TableCell>
                                <TableCell className='fontAdjusment'>
                                  <span>
                                    {item.field.field_type_id === FIELD_TYPE.checkbox ?
                                      'Checkbox'
                                      : item.field.field_type_id === FIELD_TYPE.text ?
                                        'Texto'
                                        : item.field.field_type_id === FIELD_TYPE.number ?
                                          'Numérico'
                                          : item.field.field_type_id === FIELD_TYPE.dropdown ?
                                            'Dropdown'
                                            : item.field.field_type_id === FIELD_TYPE.radio ?
                                              'Radio'
                                              : item.field.field_type_id === FIELD_TYPE.image ?
                                                'Imagem'
                                                : item.field.field_type_id === FIELD_TYPE.counter ?
                                                  'Contador'
                                                  : item.field.field_type_id === FIELD_TYPE.datetime ?
                                                    'Data/hora'
                                                    : item.field.field_type_id === FIELD_TYPE.label ?
                                                      'Label'
                                                      : 'Desconhecido'
                                    }
                                  </span>
                                </TableCell>
                                <TableCell className='fontAdjusment'>
                                  <span >
                                    {item.field.owner_id}
                                  </span>
                                </TableCell>
                                <TableCell className='fontAdjusment'>
                                  <span>
                                    {item.field?.order !== undefined ? item.field?.order + 1 : null}
                                  </span>
                                </TableCell>
                                {userData.admin && (
                                  <TableCell sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title={t('group-table-action-edit')}>
                                      <Pencil onClick={() => onEdit(item.field)} size={25} color={theme.colors.main} cursor='pointer' />
                                    </Tooltip>

                                    <Tooltip title={t('group-table-action-delete')}>
                                      <TrashSimple onClick={() => handleDeleteField(item.field)} size={25} color={theme.colors.red} cursor='pointer' />
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            </React.Fragment>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
            {fields?.length === 0 && (
              <div style={{ height: '30vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold' }}>{t('projects-forms-warning')}</span>
              </div>
            )}
          </TableContainer>
        </>
      )}

      {modalSelect.text && (
        <TextFieldDataModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.label && (
        <LabelFieldDataModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.number && (
        <NumberFieldDataModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.image && (
        <ImageFieldModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.counter && (
        <CounterFieldModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.datetime && (
        <DatetimeFieldDataModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}

      {modalSelect.options && (
        <FieldOptionsDataModal
          isEditFieldModalOpen={isEditFieldModalOpen}
          dataField={onEditField}
          fieldTypeValue={fieldTypeValue}
          getProjectData={getProjectData}
          fieldsLength={fieldsLength}
        />
      )}
      <DialogAlert
        handleDelete={handleDeleteFieldConfirmed}
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        dialogTitle={t('delete-option-field')}
        dialogMessage={t('delete-verification-field', {fieldRowTitle})}
      />
    </>
  );
}
